import { useState } from 'react'
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion'
import LeftPanel from './components/LeftPanel'
import CategoryDetailPanel from './components/CategoryDetailPanel'
import DemandSensingPanel from './components/DemandSensingPanel'
import ChatPanel from './components/ChatPanel'
import NoteModal from './components/NoteModal'
import LoginPage from './components/LoginPage'
import EmilioDashboard from './components/EmilioDashboard'
import NeedsAttentionPanel from './components/NeedsAttentionPanel'
import ActivityFeedPanel from './components/ActivityFeedPanel'
import POSidePanel from './components/POSidePanel'
import MainChatPanel from './components/MainChatPanel'
import CreateAgentPanel from './components/CreateAgentPanel'
import ConnectorSettingsPanel from './components/ConnectorSettingsPanel'
import { ShipmentDetailPanel } from './components/PODashboard'

const AGENT_PREFILL = {
  "Status & Tracking": {
    name: "Status & Tracking",
    connectors: ["ERP System", "EDI", "PO Database"],
    connectorConfig: {
      "ERP System": { table: "purchase_orders", row: "status, eta, po_number", column: "current_status" },
      "EDI": { table: "edi_856_ship_notices", row: "ship_date, carrier, tracking_no", column: "shipment_status" },
      "PO Database": { table: "po_line_items", row: "po_id, quantity, unit_price", column: "fulfillment_status" },
    },
    goal: "Monitor all active purchase orders and shipments in real time. Detect delays, ETA drift, and carrier exceptions before they impact surgical cases.",
    guide: "Check \\ERP System for PO status updates every 15 minutes. Cross-reference with \\EDI for carrier scan updates and tracking numbers. When ETAs shift by more than 4 hours or a carrier exception is logged in \\PO Database, flag the shipment as at-risk. If the delayed shipment is tied to a same-day OR case, notify the OR coordinator and run the resolution playbook. Document all actions taken in the activity log.",
    trigger: "event",
    eventVal: "When a shipment ETA changes or a carrier exception is logged",
    autonomy: 1,
  },
}

export default function App() {
  const [currentUser, setCurrentUser] = useState(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [chatOpen, setChatOpen] = useState(false)
  const [noteOpen, setNoteOpen] = useState(false)
  const [mainChatOpen, setMainChatOpen] = useState(false)
  const [selectedAgent, setSelectedAgent] = useState(null)
  const [createAgentOpen, setCreateAgentOpen] = useState(false)
  const [editingAgentData, setEditingAgentData] = useState(null)
  const [connectorSettingsOpen, setConnectorSettingsOpen] = useState(false)
  const [selectedShipment, setSelectedShipment] = useState(null)
  const [agentAutonomyMap, setAgentAutonomyMap] = useState({})

  const handleLogout = () => {
    setDetailOpen(false)
    setChatOpen(false)
    setNoteOpen(false)
    setCurrentUser(null)
  }

  const isEmilio = currentUser?.email === 'emilio@its.jnj.com'

  const handleCloseDetail = () => {
    setDetailOpen(false)
    setChatOpen(false)
    setNoteOpen(false)
  }

  return (
    <AnimatePresence mode="wait">
      {!currentUser ? (
        <LoginPage key="login" onLogin={(user) => setCurrentUser(user)} />
      ) : isEmilio ? (
        <EmilioDashboard key="emilio" onLogout={handleLogout} />
      ) : (
        <motion.div
          key="app"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          style={{
            width: '100vw',
            height: '100vh',
            background: '#242424',
            display: 'flex',
            padding: 9,
            gap: 8,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <LayoutGroup>
            <AnimatePresence>
              {connectorSettingsOpen && selectedGroup?.type === 'Purchase Orders' && (
                <ConnectorSettingsPanel
                  key="connector-settings"
                  onClose={() => setConnectorSettingsOpen(false)}
                />
              )}
            </AnimatePresence>
            <AnimatePresence>
              {selectedGroup && (
                <CategoryDetailPanel
                  key="category-detail"
                  group={selectedGroup}
                  onClose={() => { setSelectedGroup(null); setSelectedAgent(null); setCreateAgentOpen(false); setEditingAgentData(null); setSelectedShipment(null); setConnectorSettingsOpen(false) }}
                  onAgentSelect={setSelectedAgent}
                  onAddAgent={() => { setEditingAgentData(null); setCreateAgentOpen(v => !v) }}
                  onEditAgent={(agent) => {
                    const prefill = AGENT_PREFILL[agent.name]
                    setEditingAgentData(prefill ?? agent)
                    setCreateAgentOpen(true)
                  }}
                  onSettingsClick={() => setConnectorSettingsOpen(true)}
                />
              )}
            </AnimatePresence>
            <AnimatePresence>
              {createAgentOpen && selectedGroup && (
                <CreateAgentPanel
                  key={editingAgentData?.name ?? 'new-agent'}
                  initialData={editingAgentData ? { ...editingAgentData, autonomy: agentAutonomyMap[editingAgentData.name] ?? editingAgentData.autonomy ?? 0 } : null}
                  onClose={() => { setCreateAgentOpen(false); setEditingAgentData(null) }}
                  onAutonomyChange={(name, level) => setAgentAutonomyMap(prev => ({ ...prev, [name]: level }))}
                  onSubmit={(data) => {
                    setCreateAgentOpen(false)
                    setEditingAgentData(null)
                  }}
                />
              )}
            </AnimatePresence>
            <LeftPanel
              collapsed={noteOpen}
              onLogout={handleLogout}
              onCategoryClick={(group) => { setSelectedGroup(group); setSelectedAgent(group.agents[0]?.name ?? null) }}
              selectedAgent={selectedAgent}
              selectedGroup={selectedGroup}
              onBack={() => { setSelectedGroup(null); setSelectedShipment(null) }}
              onChatToggle={() => setMainChatOpen(v => !v)}
              onShipmentSelect={setSelectedShipment}
              agentAutonomyMap={agentAutonomyMap}
            />
            <AnimatePresence>
              {mainChatOpen && (
                <MainChatPanel
                  key="main-chat"
                  onClose={() => setMainChatOpen(false)}
                />
              )}
            </AnimatePresence>
            <AnimatePresence>
              {selectedShipment && (
                <ShipmentDetailPanel
                  key={selectedShipment.po}
                  row={selectedShipment}
                  onClose={() => setSelectedShipment(null)}
                  autonomyLevel={agentAutonomyMap['Status & Tracking'] ?? 0}
                />
              )}
            </AnimatePresence>
            <motion.div
              layout
              animate={{
                width: selectedShipment ? 0 : 294,
                opacity: selectedShipment ? 0 : 1,
                background: selectedGroup?.type === 'Purchase Orders' ? 'rgba(0,0,0,0.5)' : 'transparent',
                borderRadius: 4,
              }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              style={{
                flexShrink: 0,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <AnimatePresence mode="wait">
                {selectedGroup?.type === 'Purchase Orders' ? (
                  <POSidePanel key="po-side" selectedAgent={selectedAgent} />
                ) : (
                  <motion.div
                    key="default-side"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{ display: 'flex', flexDirection: 'column', gap: 8, height: '100%' }}
                  >
                    <NeedsAttentionPanel detailOpen={detailOpen} onDemandSensingClick={() => setDetailOpen(true)} />
                    <ActivityFeedPanel />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
            <AnimatePresence>
              {detailOpen && (
                <DemandSensingPanel
                  key="demand"
                  onClose={handleCloseDetail}
                  onChatWithAgent={() => setChatOpen(true)}
                />
              )}
            </AnimatePresence>
            <AnimatePresence>
              {chatOpen && (
                <ChatPanel
                  key="chat"
                  onClose={() => setChatOpen(false)}
                  onNoteReady={() => setNoteOpen(true)}
                />
              )}
            </AnimatePresence>
            <AnimatePresence>
              {noteOpen && (
                <NoteModal
                  key="note"
                  onSend={() => setNoteOpen(false)}
                  onCancel={() => setNoteOpen(false)}
                />
              )}
            </AnimatePresence>
          </LayoutGroup>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
